import MurmurHash3 from "imurmurhash";

export interface IPhraseOptions {
  plural?: string;
  comments?: string[];
  context?: string;
  count?: number;
  default?: string;
  [customProp: string]: any;
}

export interface IPhraseConstructorArgs {
  msgId: string;
  msgIdPlural?: string;
  msgCtxt?: string;
  comments?: string[];
  msgStr?: string[];
}

export class Phrase {
  // RAP-163: Unify prefix/suffix token regex configuration
  // https://ifitdev.atlassian.net/browse/RAP-163
  public static readonly TOKEN_REGEX = new RegExp("%{(.*?)}", "gm");

  public static genPhraseKey(
    phraseStr: string,
    phraseStrPlural: string,
    tokenRegEx: RegExp = Phrase.TOKEN_REGEX
  ): string {
    const tokens = this.getTokensFromPhrase(
      phraseStr,
      phraseStrPlural,
      tokenRegEx
    );
    const indexedPhrase = this._indexPhrase(phraseStr, tokens, tokenRegEx);
    return Phrase._genHash(indexedPhrase);
  }

  public static getTokensFromPhrase(
    phraseStr: string,
    phraseStrPlural: string,
    tokenRegEx: RegExp = Phrase.TOKEN_REGEX
  ): string[] {
    const tokens = new Set<string>();

    /* To Update with node version: Because of the inability of String.prototype.match() to handle capture
     * groups and not being able to use String.prototype.matchAll() because of our node version (10) its
     * easier to use String.prototype.replace() here and ignore its result.
     */
    phraseStr.replace(tokenRegEx, (subStr, token: string) => {
      tokens.add(token);
      return token;
    });

    /**
     * There is an edge case where the plural and singular versions of the phrase have different tokens. We
     * need to find all the tokens in both the singular and plural phrases to index them correctly
     */
    if (phraseStrPlural) {
      phraseStrPlural.replace(tokenRegEx, (subStr, token: string) => {
        tokens.add(token);
        return token;
      });
    }

    return [...tokens];
  }

  public static packageForPolyglot(phrases: Phrase[]): any {
    const polyPhrases = phrases.map(phrase => phrase.toPolyglotPhraseObj());

    return polyPhrases.reduce((prev, curr) => {
      return { ...prev, ...curr };
    }, {});
  }

  private static _genPolyPhrase(
    phraseStr: string,
    phraseStrPlural: string,
    msgStr: string[]
  ): string {
    const polyglotPluralSeparator = " |||| ";

    if (!msgStr || !msgStr.length) {
      return "";
    }

    const tokens = this.getTokensFromPhrase(
      phraseStr,
      phraseStrPlural,
      this.TOKEN_REGEX
    );

    if (msgStr.length === 1) {
      return this._indexPhrase(msgStr[0], tokens, this.TOKEN_REGEX);
    }

    return msgStr
      .filter(str => str.trim() !== "")
      .map(str => this._indexPhrase(str, tokens, this.TOKEN_REGEX).trim())
      .join(polyglotPluralSeparator);
  }

  private static _indexPhrase(
    phrase: string,
    tokens: string[],
    tokenRegEx: RegExp = Phrase.TOKEN_REGEX
  ): string {
    if (!tokens) {
      return phrase;
    }

    return phrase.replace(tokenRegEx, (subStr, token: string) => {
      return `%{_${tokens.indexOf(token)}_}`;
    });
  }

  private static _genHash(msgId: string): string {
    return `r#${MurmurHash3(msgId).result()}`;
  }

  public readonly msgId: string;
  public readonly msgIdPlural: string;
  public readonly msgCtxt: string;
  public readonly comments: string[];
  public readonly msgStr: string[];
  public readonly phraseKey: string;
  public readonly polyPhrase: string;

  constructor(args: IPhraseConstructorArgs) {
    this.msgId = args.msgId;
    this.msgIdPlural = args.msgIdPlural;
    this.msgCtxt = args.msgCtxt;
    this.comments = args.comments;
    this.msgStr = args.msgStr;

    this.phraseKey = this.msgId
      ? Phrase.genPhraseKey(this.msgId, this.msgIdPlural)
      : null;
    this.polyPhrase = this.msgId
      ? Phrase._genPolyPhrase(this.msgId, this.msgIdPlural, this.msgStr)
      : null;
  }

  public toNativePhrase(): Phrase {
    // Copy original to translated
    const msgStr = [this.msgId];
    if (this.msgIdPlural) {
      msgStr.push(this.msgIdPlural);
    }

    return new Phrase({
      msgId: this.msgId,
      msgIdPlural: this.msgIdPlural,
      msgCtxt: this.msgCtxt,
      comments: this.comments,
      msgStr
    });
  }

  public toTestPhrase(): Phrase {
    // Copy original to translated
    const msgStr = [`⌈${this.msgId}⌋`];
    if (this.msgIdPlural) {
      msgStr.push(`⌈${this.msgIdPlural}⌋`);
    }

    return new Phrase({
      msgId: this.msgId,
      msgIdPlural: this.msgIdPlural,
      msgCtxt: this.msgCtxt,
      comments: this.comments,
      msgStr
    });
  }

  public toPolyglotPhraseObj(): { [key: string]: string } {
    return { [this.phraseKey]: this.polyPhrase };
  }
}
